/*!
 * Copyright © 2019. Verizon Connect Ireland Limited. All rights reserved.
 */

import { FlatTreeControl } from '@angular/cdk/tree';
import { Observable, Subject } from 'rxjs';
import { ECheckboxState } from '../../../models/checkbox-state.enum';

import { FlatDataUtilities, IFlatTreeNode } from '../shared';

export class ScopingTreeSelectionModel<T> {
  selectionChange$: Observable<IFlatTreeNode<T>[]>;

  private readonly _selectionChangeSubject = new Subject<IFlatTreeNode<T>[]>();

  constructor(private readonly _treeControl: FlatTreeControl<IFlatTreeNode<T>>) {
    this.selectionChange$ = this._selectionChangeSubject.asObservable();
    this._initSelection();
  }

  toggle(node: IFlatTreeNode<T>): void {
    const shouldSelect = node.state !== ECheckboxState.Checked;
    const descendants = FlatDataUtilities.getDescendants(this._treeControl.dataNodes, node);

    if (shouldSelect) {
      node.state = ECheckboxState.Checked;
      this._select(...descendants);
    } else {
      node.state = ECheckboxState.Unchecked;
      this._deselect(...descendants);
    }

    this._updateNodeAncestorsStateBasedOnChildrenState(node);

    this._emitSelection();
  }

  getSelectedNodes(): IFlatTreeNode<T>[] {
    const selectedNodes = this._treeControl.dataNodes.filter(node => node.state === ECheckboxState.Checked);
    return selectedNodes;
  }

  private _initSelection(): void {
    const nodesCount = this._treeControl.dataNodes.length;
    for (let i = nodesCount - 1; i >= 0; i--) {
      const node = this._treeControl.dataNodes[i];
      if (node.isExpandable) {
        this._updateNodeStateBasedOnChildren(node);
      }
    }
  }

  private _updateNodeAncestorsStateBasedOnChildrenState(node: IFlatTreeNode<T>): void {
    const index = this._treeControl.dataNodes.indexOf(node);
    const startingIndex = node.isExpandable ? index : index - 1;
    let currentLevel = node.isExpandable ? node.level + 1 : node.level;
    for (let i = startingIndex; i >= 0; i--) {
      const currentNode = this._treeControl.dataNodes[i];
      if (currentNode.isExpandable && currentNode.level === currentLevel - 1) {
        this._updateNodeStateBasedOnChildren(currentNode);
        currentLevel--;
      }
    }
  }

  private _updateNodeStateBasedOnChildren(node: IFlatTreeNode<T>): void {
    const children = FlatDataUtilities.getChildrenByNode(this._treeControl.dataNodes, node);
    const areAllSelected = FlatDataUtilities.areAllItemsInState(children, ECheckboxState.Checked);

    if (areAllSelected) {
      this._select(node);
    } else {
      const arePartiallySelected = FlatDataUtilities.areItemsPartiallySelected(children);

      if (arePartiallySelected) {
        node.state = ECheckboxState.Indeterminate;
      } else {
        this._deselect(node);
      }
    }
  }

  private _select(...nodes: IFlatTreeNode<T>[]): void {
    nodes.forEach(node => {
      node.state = ECheckboxState.Checked;
    });
  }

  private _deselect(...nodes: IFlatTreeNode<T>[]): void {
    nodes.forEach(node => {
      node.state = ECheckboxState.Unchecked;
    });
  }

  private _emitSelection(): void {
    const selectedNodes = this.getSelectedNodes();
    this._selectionChangeSubject.next(selectedNodes);
  }
}
